import React, { useEffect, useMemo, useState } from "react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	ResponsiveContainer,
	BarChart,
	Bar,
	PieChart,
	Pie,
	Cell,
	CartesianGrid,
} from "recharts";
import {
	CheckCircle2,
	Clock3,
	Loader2,
	PauseCircle,
	Users,
} from "lucide-react";
import CreateTicketModal from "../../../Modal/CreateTicketModal";
import FrameBG from "../../../../Assets/FrameBGDashboard.png";
import {
	GetUserWiseYearlyTaskReport,
	GetYearlyTaskReport,
} from "../../AdminServices/AdminReportService";

const STATUS_COLORS = {
	totalTasks: "#003a63",
	closedTasks: "#FFD166",
	openTasks: "#D1E3FF",
	inProgressTasks: "#8ECAE6",
	holdTasks: "#D1E3FF",
	underReviewTasks: "#34d399",
};

const SURFACE_COLORS = {
	primary: "#003a63",
	secondary: "#13659C",
	soft: "#BCD4E8",
	pale: "#D1E3FF",
	accent: "#FFD166",
	accentSoft: "#FFF1BF",
	teal: "#8ECAE6",
	tealDark: "#219EBC",
	border: "#D9E7F2",
};

const getCreatedCount = (row) => row?.totalTasks ?? row?.createdTasks ?? 0;
const getUnderReviewCount = (row) =>
	row?.underReviewTasks ?? row?.completedTasks ?? 0;

const AdminHome = () => {
	const currentYear = new Date().getFullYear();
	const [showModal, setShowModal] = useState(false);
	const [selectedYear, setSelectedYear] = useState(String(currentYear));
	const [reportView, setReportView] = useState("month");
	const [reportScope, setReportScope] = useState("overall");
	const [selectedUserId, setSelectedUserId] = useState("");
	const [yearlyReportRows, setYearlyReportRows] = useState([]);
	const [userWiseReportRows, setUserWiseReportRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		const fetchDashboardReports = async () => {
			try {
				setLoading(true);
				setError("");

				const [yearlyRes, userWiseRes] = await Promise.all([
					GetYearlyTaskReport({ year: selectedYear }),
					GetUserWiseYearlyTaskReport({ year: selectedYear }),
				]);

				setYearlyReportRows(yearlyRes?.data?.taskReport || []);
				setUserWiseReportRows(userWiseRes?.data?.taskReport || []);
			} catch (err) {
				console.error("Failed to fetch admin dashboard reports", err);
				setYearlyReportRows([]);
				setUserWiseReportRows([]);
				setError("Unable to load yearly report data for the dashboard.");
			} finally {
				setLoading(false);
			}
		};

		fetchDashboardReports();
	}, [selectedYear]);

	const yearOptions = Array.from({ length: 6 }, (_, index) =>
		String(currentYear - index),
	);

	const {
		overallTotals,
		monthlyTrendData,
		statusPieData,
		userOptions,
		userTotals,
		userChartData,
		selectedUserMonthlyRows,
		selectedUserMonthlyChartData,
		selectedUserTotals,
		topPerformer,
	} = useMemo(() => {
		const overallRows = Array.isArray(yearlyReportRows) ? yearlyReportRows : [];
		const userRows = Array.isArray(userWiseReportRows)
			? userWiseReportRows
			: [];
		const totals = overallRows.find((row) => row.monthNo === 13) || null;
		const monthlyRows = overallRows
			.filter((row) => row.monthNo !== 13)
			.sort((a, b) => a.monthNo - b.monthNo);

		const users = userRows
			.filter((row) => row.monthNo === 13)
			.sort((a, b) => getCreatedCount(b) - getCreatedCount(a));
		const availableUsers = users.map((row) => ({
			userId: String(row.userId),
			userName: row.userName,
		}));
		const activeUserId = selectedUserId || availableUsers[0]?.userId || "";
		const selectedUserRows = userRows
			.filter((row) => String(row.userId) === activeUserId)
			.sort((a, b) => a.monthNo - b.monthNo);
		const selectedUserTotalRow =
			selectedUserRows.find((row) => row.monthNo === 13) || null;
		const selectedUserMonthRows = selectedUserRows.filter(
			(row) => row.monthNo !== 13,
		);

		const bestUser = [...users].sort((a, b) => {
			const aCreated = getCreatedCount(a);
			const bCreated = getCreatedCount(b);
			const aRate = aCreated ? a.closedTasks / aCreated : 0;
			const bRate = bCreated ? b.closedTasks / bCreated : 0;

			return bRate - aRate || b.closedTasks - a.closedTasks;
		})[0];

		return {
			overallTotals: totals,
			monthlyTrendData: monthlyRows.map((row) => ({
				month: row.monthName,
				Created: getCreatedCount(row),
				Closed: row.closedTasks,
				"Under Review": getUnderReviewCount(row),
				Open: row.openTasks,
				"In Progress": row.inProgressTasks,
				Hold: row.holdTasks,
			})),
			statusPieData: totals
				? [
						{
							name: "Under Review",
							value: getUnderReviewCount(totals),
							color: STATUS_COLORS.underReviewTasks,
						},
						{
							name: "Closed",
							value: totals.closedTasks,
							color: STATUS_COLORS.closedTasks,
						},
						{
							name: "Open",
							value: totals.openTasks,
							color: STATUS_COLORS.openTasks,
						},
						{
							name: "In Progress",
							value: totals.inProgressTasks,
							color: STATUS_COLORS.inProgressTasks,
						},
						{
							name: "Hold",
							value: totals.holdTasks,
							color: STATUS_COLORS.holdTasks,
						},
					].filter((item) => item.value > 0)
				: [],
			userOptions: availableUsers,
			userTotals: users,
			userChartData: users.slice(0, 6).map((user) => ({
				name: user.userName,
				Created: getCreatedCount(user),
				Closed: user.closedTasks,
				"Under Review": getUnderReviewCount(user),
				Open: user.openTasks,
			})),
			selectedUserMonthlyRows: selectedUserMonthRows,
			selectedUserMonthlyChartData: selectedUserMonthRows.map((row) => ({
				month: row.monthName,
				Created: getCreatedCount(row),
				Closed: row.closedTasks,
				"Under Review": getUnderReviewCount(row),
				Open: row.openTasks,
				"In Progress": row.inProgressTasks,
				Hold: row.holdTasks,
			})),
			selectedUserTotals: selectedUserTotalRow,
			topPerformer: bestUser || null,
		};
	}, [yearlyReportRows, userWiseReportRows, selectedUserId]);

	useEffect(() => {
		if (!userOptions.length) {
			if (selectedUserId) {
				setSelectedUserId("");
			}
			return;
		}

		const hasSelectedUser = userOptions.some(
			(user) => user.userId === selectedUserId,
		);

		if (!selectedUserId || !hasSelectedUser) {
			setSelectedUserId(userOptions[0].userId);
		}
	}, [userOptions, selectedUserId]);

	const summaryCards = [
		{
			label: "Created Tasks",
			value: getCreatedCount(overallTotals),
			icon: Users,
		},
		{
			label: "Closed Tasks",
			value: overallTotals?.closedTasks ?? 0,
			icon: CheckCircle2,
		},
		{
			label: "Under Review",
			value: getUnderReviewCount(overallTotals),
			icon: CheckCircle2,
		},
		{
			label: "In Progress",
			value: overallTotals?.inProgressTasks ?? 0,
			icon: Clock3,
		},
		{
			label: "Hold Tasks",
			value: overallTotals?.holdTasks ?? 0,
			icon: PauseCircle,
		},
	];

	const totalCreated = getCreatedCount(overallTotals);
	const closureRate = totalCreated
		? `${Math.round((overallTotals.closedTasks / totalCreated) * 100)}%`
		: "0%";
	const pendingCount =
		(overallTotals?.openTasks ?? 0) + (overallTotals?.inProgressTasks ?? 0);

	return (
		<>
			<div className="space-y-6">
				<div
					className="relative rounded-lg overflow-hidden shadow-md text-white mt-6"
					style={{
						backgroundImage: `url(${FrameBG})`,
						backgroundSize: "cover",
						backgroundPosition: "center",
					}}
				>
					<div className="p-8 flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
						<div>
							<h2 className="text-xl font-semibold">Admin yearly insights</h2>
							<p className="text-sm opacity-90">
								Live overview of task performance and team delivery for{" "}
								{selectedYear}.
							</p>
						</div>
						<div className="flex items-center gap-3">
							<div
								className="flex rounded-lg border overflow-hidden bg-white"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<button
									type="button"
									onClick={() => setReportView("month")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportView === "month"
												? SURFACE_COLORS.soft
												: "transparent",
										color:
											reportView === "month"
												? SURFACE_COLORS.primary
												: SURFACE_COLORS.secondary,
									}}
								>
									Month Wise
								</button>
								<button
									type="button"
									onClick={() => setReportView("year")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportView === "year"
												? SURFACE_COLORS.soft
												: "transparent",
										color:
											reportView === "year"
												? SURFACE_COLORS.primary
												: SURFACE_COLORS.secondary,
									}}
								>
									Year Wise
								</button>
							</div>
							<div
								className="flex rounded-lg border overflow-hidden bg-white"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<button
									type="button"
									onClick={() => setReportScope("overall")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportScope === "overall"
												? SURFACE_COLORS.soft
												: "transparent",
										color:
											reportScope === "overall"
												? SURFACE_COLORS.primary
												: SURFACE_COLORS.secondary,
									}}
								>
									Overall
								</button>
								<button
									type="button"
									onClick={() => setReportScope("user")}
									className="px-4 py-2 text-sm font-medium transition-colors"
									style={{
										backgroundColor:
											reportScope === "user"
												? SURFACE_COLORS.soft
												: "transparent",
										color:
											reportScope === "user"
												? SURFACE_COLORS.primary
												: SURFACE_COLORS.secondary,
									}}
								>
									User Wise
								</button>
							</div>
							<select
								value={selectedYear}
								onChange={(e) => setSelectedYear(e.target.value)}
								className="rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none border"
								style={{
									boxShadow: "none",
									borderColor: SURFACE_COLORS.border,
								}}
							>
								{yearOptions.map((year) => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</select>
							<button
								onClick={() => setShowModal(true)}
								className="bg-[#003a63] text-white px-5 py-2 rounded-lg"
							>
								Add Ticket
							</button>
						</div>
					</div>
				</div>

				<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
					<div>
						<h3 className="text-xl font-semibold text-gray-700">Insights</h3>
						<p className="text-sm text-gray-500">
							{reportView === "month"
								? reportScope === "overall"
									? "Month-wise task overview for the selected year"
									: "Month-wise task progress for the selected team member"
								: reportScope === "overall"
									? "Year-wise task summary for the selected year"
									: "Year-wise user performance summary for the selected year"}
						</p>
					</div>
					<div className="text-sm text-gray-500">
						{loading
							? "Refreshing report data..."
							: `${selectedYear} dashboard`}
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
					{summaryCards.map((card) => {
						const Icon = card.icon;

						return (
							<div
								key={card.label}
								className="bg-white p-4 rounded-lg shadow flex justify-between items-center border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<div>
									<p className="text-gray-500">{card.label}</p>
									<h2
										className="text-2xl font-bold mt-1"
										style={{ color: SURFACE_COLORS.primary }}
									>
										{card.value}
									</h2>
								</div>
								<div
									className="p-2 rounded-full"
									style={{
										backgroundColor: SURFACE_COLORS.pale,
										color: SURFACE_COLORS.primary,
									}}
								>
									<Icon className="w-8 h-8" />
								</div>
							</div>
						);
					})}
				</div>

				{reportView === "month" && reportScope === "overall" ? (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{ borderColor: SURFACE_COLORS.border }}
						>
							<div className="flex justify-between items-center mb-3">
								<div>
									<h3 className="font-semibold">Monthly task trend</h3>
									<p className="text-sm text-gray-500">
										Track created, closed, under review and active tasks across each month
									</p>
								</div>
								<span
									className="text-sm font-medium px-3 py-1 rounded-full"
									style={{
										backgroundColor: SURFACE_COLORS.accentSoft,
										color: SURFACE_COLORS.primary,
									}}
								>
									Closure rate {closureRate}
								</span>
							</div>
							<div className="h-[280px]">
								{loading ? (
									<div className="h-full flex items-center justify-center text-gray-500">
										<Loader2 className="animate-spin mr-2" size={18} />
										Loading monthly report...
									</div>
								) : monthlyTrendData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={monthlyTrendData}>
											<CartesianGrid
												strokeDasharray="3 3"
												vertical={false}
												stroke={SURFACE_COLORS.border}
											/>
											<XAxis dataKey="month" />
											<YAxis allowDecimals={false} />
											<Tooltip />
											<Legend />
											<Line
												type="monotone"
												dataKey="Created"
												stroke={STATUS_COLORS.totalTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Closed"
												stroke={STATUS_COLORS.closedTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Under Review"
												stroke={STATUS_COLORS.underReviewTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Open"
												stroke={STATUS_COLORS.openTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="In Progress"
												stroke={SURFACE_COLORS.tealDark}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Hold"
												stroke={SURFACE_COLORS.soft}
												strokeWidth={2.5}
											/>
										</LineChart>
									</ResponsiveContainer>
								) : (
									<div className="h-full flex items-center justify-center text-gray-400">
										No monthly task data available
									</div>
								)}
							</div>
						</div>

						<div
							className="bg-white p-6 rounded-lg shadow-md flex flex-col border"
							style={{ borderColor: SURFACE_COLORS.border }}
						>
							<div className="flex justify-between items-center mb-4">
								<div>
									<h3 className="font-semibold text-gray-700">
										Monthly status split
									</h3>
									<p className="text-sm text-gray-500">
										Overall status distribution for the selected year
									</p>
								</div>
								<span className="text-sm text-gray-500">
									{getCreatedCount(overallTotals)} created tasks
								</span>
							</div>

							<div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-6">
								<div className="relative w-full max-w-[420px] h-[280px]">
									{loading ? (
										<div className="h-full flex items-center justify-center text-gray-500">
											<Loader2 className="animate-spin mr-2" size={18} />
											Loading status split...
										</div>
									) : statusPieData.length > 0 ? (
										<>
											<ResponsiveContainer width="100%" height="100%">
												<PieChart>
													<Pie
														data={statusPieData}
														dataKey="value"
														nameKey="name"
														cx="50%"
														cy="50%"
														innerRadius={80}
														outerRadius={110}
														paddingAngle={3}
													>
														{statusPieData.map((entry) => (
															<Cell key={entry.name} fill={entry.color} />
														))}
													</Pie>
													<Tooltip />
												</PieChart>
											</ResponsiveContainer>
											<div className="absolute inset-0 flex flex-col items-center justify-center">
												<h2
													className="text-4xl font-bold"
													style={{ color: SURFACE_COLORS.primary }}
												>
													{closureRate}
												</h2>
												<p className="text-gray-500 text-sm mt-1 text-center">
													closed task rate
												</p>
											</div>
										</>
									) : (
										<div className="h-full flex items-center justify-center text-gray-400">
											No status data available
										</div>
									)}
								</div>

								<div className="w-full max-w-xs space-y-2">
									{statusPieData.map((item) => (
										<div
											key={item.name}
											className="flex items-center justify-between"
										>
											<div className="flex items-center space-x-2">
												<div
													className="w-3 h-3 rounded-sm"
													style={{ backgroundColor: item.color }}
												></div>
												<p className="text-gray-700 text-sm">{item.name}</p>
											</div>
											<p className="text-sm font-medium text-gray-700">
												{item.value}
											</p>
										</div>
									))}
									<div
										className="pt-3 mt-3 border-t text-sm text-gray-500"
										style={{ borderColor: SURFACE_COLORS.border }}
									>
										Top performer: {topPerformer?.userName || "N/A"}
									</div>
								</div>
							</div>
						</div>
					</div>
				) : reportView === "month" && reportScope === "user" ? (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{ borderColor: SURFACE_COLORS.border }}
						>
							<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
								<div>
									<h3 className="font-semibold">User monthly trend</h3>
									<p className="text-sm text-gray-500">
										Track month-wise task movement for the selected user
									</p>
								</div>
								<select
									value={selectedUserId}
									onChange={(e) => setSelectedUserId(e.target.value)}
									className="rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none border"
									style={{
										boxShadow: "none",
										borderColor: SURFACE_COLORS.border,
									}}
								>
									{userOptions.map((user) => (
										<option key={user.userId} value={user.userId}>
											{user.userName}
										</option>
									))}
								</select>
							</div>
							<div className="h-[280px]">
								{loading ? (
									<div className="h-full flex items-center justify-center text-gray-500">
										<Loader2 className="animate-spin mr-2" size={18} />
										Loading user monthly report...
									</div>
								) : selectedUserMonthlyChartData.length > 0 ? (
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={selectedUserMonthlyChartData}>
											<CartesianGrid
												strokeDasharray="3 3"
												vertical={false}
												stroke={SURFACE_COLORS.border}
											/>
											<XAxis dataKey="month" />
											<YAxis allowDecimals={false} />
											<Tooltip />
											<Legend />
											<Line
												type="monotone"
												dataKey="Created"
												stroke={STATUS_COLORS.totalTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Closed"
												stroke={STATUS_COLORS.closedTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Under Review"
												stroke={STATUS_COLORS.underReviewTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Open"
												stroke={STATUS_COLORS.openTasks}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="In Progress"
												stroke={SURFACE_COLORS.tealDark}
												strokeWidth={2.5}
											/>
											<Line
												type="monotone"
												dataKey="Hold"
												stroke={SURFACE_COLORS.soft}
												strokeWidth={2.5}
											/>
										</LineChart>
									</ResponsiveContainer>
								) : (
									<div className="h-full flex items-center justify-center text-gray-400">
										No user monthly data available
									</div>
								)}
							</div>
						</div>

						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{ borderColor: SURFACE_COLORS.border }}
						>
							<div className="flex items-center justify-between mb-3">
								<div>
									<h3 className="font-semibold">User monthly breakdown</h3>
									<p className="text-sm text-gray-500">
										Monthly task summary for the selected user
									</p>
								</div>
								<span
									className="text-sm font-medium px-3 py-1 rounded-full"
									style={{
										backgroundColor: SURFACE_COLORS.accentSoft,
										color: SURFACE_COLORS.primary,
									}}
								>
									{getCreatedCount(selectedUserTotals)} created tasks
								</span>
							</div>
							<div className="overflow-x-auto">
								<table className="min-w-full text-sm">
									<thead>
										<tr
											className="border-b text-left text-gray-500"
											style={{ borderColor: SURFACE_COLORS.border }}
										>
											<th className="py-3 pr-4 font-medium">Month</th>
											<th className="py-3 px-4 font-medium">Created</th>
											<th className="py-3 px-4 font-medium">Closed</th>
											<th className="py-3 px-4 font-medium">Under Review</th>
											<th className="py-3 px-4 font-medium">Hold</th>
											<th className="py-3 px-4 font-medium">Open</th>
											<th className="py-3 px-4 font-medium">In Progress</th>
										</tr>
									</thead>
									<tbody>
										{selectedUserMonthlyRows.map((row) => (
											<tr
												key={`${row.userId}-${row.monthNo}`}
												className="border-b last:border-b-0"
												style={{ borderColor: SURFACE_COLORS.border }}
											>
												<td className="py-3 pr-4 font-medium text-gray-700">
													{row.monthName}
												</td>
												<td className="py-3 px-4">{getCreatedCount(row)}</td>
												<td className="py-3 px-4">{row.closedTasks}</td>
												<td className="py-3 px-4">{getUnderReviewCount(row)}</td>
												<td className="py-3 px-4">{row.holdTasks}</td>
												<td className="py-3 px-4">{row.openTasks}</td>
												<td className="py-3 px-4">{row.inProgressTasks}</td>
											</tr>
										))}
										{selectedUserTotals && (
											<tr
												className="font-semibold"
												style={{ backgroundColor: SURFACE_COLORS.pale }}
											>
												<td className="py-3 pr-4">
													{selectedUserTotals.monthName}
												</td>
												<td className="py-3 px-4">
													{getCreatedCount(selectedUserTotals)}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.closedTasks}
												</td>
												<td className="py-3 px-4">
													{getUnderReviewCount(selectedUserTotals)}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.holdTasks}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.openTasks}
												</td>
												<td className="py-3 px-4">
													{selectedUserTotals.inProgressTasks}
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				) : reportScope === "user" ? (
					<div className="space-y-6">
						<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Active Users</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{userTotals.length}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Top Contributor</p>
								<p
									className="text-lg font-bold mt-1 truncate"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{userTotals[0]?.userName || "N/A"}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Highest Created</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{getCreatedCount(userTotals[0])}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Best Closure</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{getCreatedCount(topPerformer)
										? `${Math.round((topPerformer.closedTasks / getCreatedCount(topPerformer)) * 100)}%`
										: "0%"}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
							<div
								className="xl:col-span-2 bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
									<div>
										<h3 className="font-semibold">
											Year-wise user performance
										</h3>
										<p className="text-sm text-gray-500">
											Compare created, closed, under review and open work across the team
										</p>
									</div>
									<span
										className="text-sm font-medium px-3 py-1 rounded-full"
										style={{
											backgroundColor: SURFACE_COLORS.accentSoft,
											color: SURFACE_COLORS.primary,
										}}
									>
										Top 6 users
									</span>
								</div>

								<div className="h-[360px]">
									{loading ? (
										<div className="h-full flex items-center justify-center text-gray-500">
											<Loader2 className="animate-spin mr-2" size={18} />
											Loading year-wise report...
										</div>
									) : userChartData.length > 0 ? (
										<ResponsiveContainer width="100%" height="100%">
											<BarChart data={userChartData} barGap={10}>
												<CartesianGrid
													strokeDasharray="3 3"
													vertical={false}
													stroke={SURFACE_COLORS.border}
												/>
												<XAxis dataKey="name" />
												<YAxis allowDecimals={false} />
												<Tooltip />
												<Legend />
												<Bar
													dataKey="Created"
													fill={SURFACE_COLORS.soft}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Closed"
													fill={SURFACE_COLORS.primary}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Under Review"
													fill={STATUS_COLORS.underReviewTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Open"
													fill={SURFACE_COLORS.accent}
													radius={[6, 6, 0, 0]}
												/>
											</BarChart>
										</ResponsiveContainer>
									) : (
										<div className="h-full flex items-center justify-center text-gray-400">
											No year-wise task data available
										</div>
									)}
								</div>
							</div>

							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<h3 className="font-semibold mb-1">Top Performers</h3>
								<p className="text-sm text-gray-500 mb-4">
									Quick view of the strongest yearly contributors
								</p>
								<div className="space-y-3">
									{userTotals.slice(0, 5).map((row, index) => {
										const createdCount = getCreatedCount(row);
										const closure = createdCount
											? Math.round((row.closedTasks / createdCount) * 100)
											: 0;

										return (
											<div
												key={row.userId}
												className="rounded-lg border p-3"
												style={{
													borderColor: SURFACE_COLORS.border,
													backgroundColor:
														index === 0 ? SURFACE_COLORS.accentSoft : "#ffffff",
												}}
											>
												<div className="flex items-center justify-between gap-3">
													<div>
														<p className="font-medium text-gray-800">
															{row.userName}
														</p>
														<p className="text-xs text-gray-500">
															Created {getCreatedCount(row)} • Closed{" "}
															{row.closedTasks}
														</p>
													</div>
													<div className="text-right">
														<p
															className="text-sm font-semibold"
															style={{ color: SURFACE_COLORS.primary }}
														>
															{closure}%
														</p>
														<p className="text-xs text-gray-500">closure</p>
													</div>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>

						<div
							className="bg-white p-4 rounded-lg shadow border"
							style={{ borderColor: SURFACE_COLORS.border }}
						>
							<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-3">
								<div>
									<h3 className="font-semibold">User-wise yearly breakdown</h3>
									<p className="text-sm text-gray-500">
										Detailed yearly counts for each user
									</p>
								</div>
								<span className="text-sm text-gray-500">
									{userTotals.length} users with report data
								</span>
							</div>

							<div className="overflow-x-auto">
								<table className="min-w-full text-sm">
									<thead>
										<tr
											className="border-b text-left text-gray-500"
											style={{ borderColor: SURFACE_COLORS.border }}
										>
											<th className="py-3 pr-4 font-medium">User</th>
											<th className="py-3 px-4 font-medium">Created</th>
											<th className="py-3 px-4 font-medium">Closed</th>
											<th className="py-3 px-4 font-medium">Under Review</th>
											<th className="py-3 px-4 font-medium">Hold</th>
											<th className="py-3 px-4 font-medium">Open</th>
											<th className="py-3 px-4 font-medium">In Progress</th>
										</tr>
									</thead>
									<tbody>
										{userTotals.map((row) => (
											<tr
												key={row.userId}
												className="border-b last:border-b-0"
												style={{ borderColor: SURFACE_COLORS.border }}
											>
												<td className="py-3 pr-4 font-medium text-gray-700">
													{row.userName}
												</td>
												<td className="py-3 px-4">{getCreatedCount(row)}</td>
												<td className="py-3 px-4">{row.closedTasks}</td>
												<td className="py-3 px-4">{getUnderReviewCount(row)}</td>
												<td className="py-3 px-4">{row.holdTasks}</td>
												<td className="py-3 px-4">{row.openTasks}</td>
												<td className="py-3 px-4">{row.inProgressTasks}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				) : (
					<div className="space-y-6">
						<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Created</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{getCreatedCount(overallTotals)}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Closed</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{overallTotals?.closedTasks ?? 0}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Under Review</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{getUnderReviewCount(overallTotals)}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Pending</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{pendingCount}
								</p>
							</div>
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<p className="text-gray-500 text-sm">Closure Rate</p>
								<p
									className="text-2xl font-bold mt-1"
									style={{ color: SURFACE_COLORS.primary }}
								>
									{closureRate}
								</p>
							</div>
						</div>

						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							<div
								className="bg-white p-4 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<div className="flex justify-between items-center mb-3">
									<div>
										<h3 className="font-semibold">Year summary</h3>
										<p className="text-sm text-gray-500">
											High-level yearly status distribution
										</p>
									</div>
									<span
										className="text-sm font-medium px-3 py-1 rounded-full"
										style={{
											backgroundColor: SURFACE_COLORS.accentSoft,
											color: SURFACE_COLORS.primary,
										}}
									>
										Closure rate {closureRate}
									</span>
								</div>
								<div className="h-[320px]">
									{loading ? (
										<div className="h-full flex items-center justify-center text-gray-500">
											<Loader2 className="animate-spin mr-2" size={18} />
											Loading year summary...
										</div>
									) : statusPieData.length > 0 ? (
										<ResponsiveContainer width="100%" height="100%">
											<BarChart
												data={[
													{
														name: "Tasks",
														Created: getCreatedCount(overallTotals),
														Closed: overallTotals?.closedTasks ?? 0,
														"Under Review": getUnderReviewCount(overallTotals),
														Open: overallTotals?.openTasks ?? 0,
														"In Progress": overallTotals?.inProgressTasks ?? 0,
														Hold: overallTotals?.holdTasks ?? 0,
													},
												]}
											>
												<CartesianGrid
													strokeDasharray="3 3"
													vertical={false}
													stroke={SURFACE_COLORS.border}
												/>
												<XAxis dataKey="name" />
												<YAxis allowDecimals={false} />
												<Tooltip />
												<Legend />
												<Bar
													dataKey="Created"
													fill={STATUS_COLORS.totalTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Closed"
													fill={STATUS_COLORS.closedTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Under Review"
													fill={STATUS_COLORS.underReviewTasks}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Open"
													fill={SURFACE_COLORS.accent}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="In Progress"
													fill={SURFACE_COLORS.tealDark}
													radius={[6, 6, 0, 0]}
												/>
												<Bar
													dataKey="Hold"
													fill={SURFACE_COLORS.soft}
													radius={[6, 6, 0, 0]}
												/>
											</BarChart>
										</ResponsiveContainer>
									) : (
										<div className="h-full flex items-center justify-center text-gray-400">
											No year summary available
										</div>
									)}
								</div>
							</div>

							<div
								className="bg-white p-5 rounded-lg shadow border"
								style={{ borderColor: SURFACE_COLORS.border }}
							>
								<div className="flex items-center justify-between mb-4">
									<div>
										<h3 className="font-semibold">Year overview</h3>
										<p className="text-sm text-gray-500">
											Readable snapshot of created and delivery status
										</p>
									</div>
									<span
										className="text-sm font-medium px-3 py-1 rounded-full"
										style={{
											backgroundColor: SURFACE_COLORS.accentSoft,
											color: SURFACE_COLORS.primary,
										}}
									>
										{getCreatedCount(overallTotals)} created
									</span>
								</div>
								<div className="grid grid-cols-2 gap-3">
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: SURFACE_COLORS.pale }}
									>
										<p className="text-xs text-gray-500">Created</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: SURFACE_COLORS.primary }}
										>
											{getCreatedCount(overallTotals)}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: SURFACE_COLORS.accentSoft }}
									>
										<p className="text-xs text-gray-500">Closed</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: SURFACE_COLORS.primary }}
										>
											{overallTotals?.closedTasks ?? 0}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: "#EAFBF5" }}
									>
										<p className="text-xs text-gray-500">Under Review</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: SURFACE_COLORS.primary }}
										>
											{getUnderReviewCount(overallTotals)}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: "#F7FBFF" }}
									>
										<p className="text-xs text-gray-500">Open</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: SURFACE_COLORS.primary }}
										>
											{overallTotals?.openTasks ?? 0}
										</p>
									</div>
									<div
										className="rounded-lg p-4"
										style={{ backgroundColor: "#EEF7FB" }}
									>
										<p className="text-xs text-gray-500">In Progress</p>
										<p
											className="text-xl font-bold mt-1"
											style={{ color: SURFACE_COLORS.primary }}
										>
											{overallTotals?.inProgressTasks ?? 0}
										</p>
									</div>
									<div
										className="rounded-lg p-4 col-span-2"
										style={{ backgroundColor: "#F4F8FB" }}
									>
										<div className="flex items-center justify-between">
											<div>
												<p className="text-xs text-gray-500">On Hold</p>
												<p
													className="text-xl font-bold mt-1"
													style={{ color: SURFACE_COLORS.primary }}
												>
													{overallTotals?.holdTasks ?? 0}
												</p>
											</div>
											<div className="text-right">
												<p className="text-xs text-gray-500">Pending work</p>
												<p
													className="text-xl font-bold mt-1"
													style={{ color: SURFACE_COLORS.primary }}
												>
													{pendingCount}
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{error && (
					<div
						className="text-sm font-medium"
						style={{ color: SURFACE_COLORS.secondary }}
					>
						{error}
					</div>
				)}
			</div>

			<CreateTicketModal
				show={showModal}
				onClose={() => setShowModal(false)}
				onCreated={() => {}}
			/>
		</>
	);
};

export default AdminHome;
